import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, takeReturnTo, describeError } from '../lib/api.js'
import { msalEnabled, signInWithMicrosoft, leaveForMicrosoft, finishMicrosoftPage } from '../lib/msalConfig.js'
import { signInWithIce, leaveForIce, settleIceAnswer } from '../lib/iceSignin.js'
import { signsInFullPage, finishSigninReturn } from '../lib/signinPage.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import BrandRule from '../components/BrandRule.jsx'
import TenantPicker from '../components/TenantPicker.jsx'
import icefieldLogo from '../assets/icefield-logo.svg'
import icefieldLogoOnDark from '../assets/icefield-logo-on-dark.svg'
import appLockup from '../assets/crewdispatch-service-logo.png'
import appLockupOnDark from '../assets/crewdispatch-service-logo-on-dark.png'

/**
 * One centred card on the grouped background — the macOS/iOS shape for a screen with exactly
 * one thing to decide. Mark, then the single button, and nothing in between: anybody reading
 * this screen has already decided to sign in, so a line explaining what the product is was
 * answering a question nobody standing here is asking.
 *
 * There is no tenant yet at this point: nobody is signed in, so there is no brand colour to
 * apply and no company logo to show. What's shown instead is the product's own identity plus,
 * in the footer, the maker's mark on its own — this is Icefield's product, signed the way a
 * vendor signs its own login screen, not a company logo standing in for a company that
 * hasn't been chosen. The mark signs it without "Powered by" in front, which was saying in
 * words what the logo was already there to say.
 *
 * The lockup is the dark-lettered variant of the mark. The shipped artwork has its wordmark
 * in white with a glow, which is the only reason the old screen needed an onyx backdrop under
 * it; the variant re-inks the lettering and leaves the icon alone, so this screen can sit on
 * the same light surface as the rest of the app.
 *
 * Both marks come in a second copy for the dark surface (`-on-dark`), re-inked the same way
 * in the other direction: the lettering goes pale, the coloured parts — the app icon, the
 * cyan "AI" — are untouched. A CSS filter would have been a line instead of a file, but there
 * is no filter that lightens navy lettering and leaves a cyan accent where it is. The swap is
 * <picture> rather than anything in JavaScript so that the right one is the only one fetched,
 * and so that it is right in the first frame — this screen is the first thing anybody sees.
 *
 * Icefield's pair is the vector original of that, supplied as drawn rather than re-inked
 * here: two SVGs identical but for the thirteen fills of the mountains and the wordmark
 * (#00375d against a light surface, #f3fbff against a dark one), with the cyan of the "AI"
 * the same value in both. They are named for the surface they sit ON, like the lockup beside
 * them, which is the opposite of how the files arrived — a mark named for its own ink and a
 * mark named for its background read identically until the day somebody picks the wrong one.
 */
export default function Login() {
  const { refresh } = useAuth()
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [choice, setChoice] = useState(null) // { ticket, tenants[] } when one identity has several
  // How the server signs people in: the Microsoft popup straight from here, or through ICE,
  // which shows the same Microsoft prompt after its own address. The same button either way.
  const { data: config } = useQuery({ queryKey: ['auth-config'], queryFn: () => api.get('/auth/config'), retry: false })
  const viaIce = config?.signinVia === 'ice'

  // Back from a full-page sign-in (a phone's): finish it here, as the popup's answer would be.
  useEffect(() => {
    const finishing = finishSigninReturn({
      ice: async (answer) => settleIceAnswer(answer),
      microsoft: async () => api.post('/auth/microsoft', { id_token: await finishMicrosoftPage() }),
    })
    if (!finishing) return
    let live = true
    setBusy(true)
    finishing
      .then((res) => live && settle(res))
      .catch((err) => live && setError(describeError(err, 'Sign-in did not complete.')))
      .finally(() => live && setBusy(false))
    return () => {
      live = false
    }
  }, [])

  // Pressing Back on Microsoft's page can bring this page back as it was left, still busy.
  useEffect(() => {
    const onShow = (e) => e.persisted && setBusy(false)
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  async function microsoft() {
    setBusy(true)
    setError(null)
    let leaving = false
    try {
      if (signsInFullPage()) {
        // This page is about to be replaced. It stays busy until it is.
        leaving = true
        return viaIce ? leaveForIce() : await leaveForMicrosoft()
      }
      const res = viaIce
        ? await signInWithIce()
        : await api.post('/auth/microsoft', { id_token: await signInWithMicrosoft() })
      await settle(res)
    } catch (err) {
      leaving = false
      setError(describeError(err, 'Sign-in did not complete.'))
    } finally {
      if (!leaving) setBusy(false)
    }
  }

  async function settle(res) {
    // One Microsoft account can be invited into more than one company — a shared
    // subcontractor. Ask which one rather than guessing.
    if (res?.needsTenantChoice) setChoice(res)
    else await returnToWhereTheyWere()
  }

  /**
   * Back to the page the expired session interrupted.
   *
   * On a desktop the refresh alone is usually enough — sign-in is a popup and this screen
   * rendered in place, so the address bar never changed. The stored path covers the case it
   * does not: a phone's full-page sign-in, which comes back to the root.
   */
  async function returnToWhereTheyWere() {
    const back = takeReturnTo()
    await refresh()
    const here = window.location.pathname + window.location.search
    if (back && back !== here) window.history.replaceState(null, '', back)
  }

  async function pickTenant(tenantId) {
    setBusy(true)
    try {
      await api.post('/auth/microsoft/select', { ticket: choice.ticket, tenantId })
      await returnToWhereTheyWere()
    } catch (err) {
      setError(describeError(err, 'Could not sign in to that company.'))
      setChoice(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-shell">
      {/* Same rule as the signed-in app: it is the product's edge, and this is the first
          place anyone sees it. */}
      <BrandRule />

      <div className="auth-card card">
        <div className="auth-head">
          {/* The lockup carries the product's name as artwork, so there is no heading under
              it — the name set twice, once in type and once in a logo, reads as a mistake. */}
          <picture className="auth-mark">
            <source srcSet={appLockupOnDark} media="(prefers-color-scheme: dark)" />
            <img src={appLockup} alt="CrewDispatch Service" className="auth-lockup" />
          </picture>
          {/* Only ever an instruction now. The line that used to sit here described the
              product to somebody who has already decided to sign in to it. */}
          {choice && <p className="auth-tagline">Choose which company to sign in to.</p>}
        </div>

        <div className="auth-body">
          {error && <div className="error">{error}</div>}

          {choice ? (
            <div className="login-tenants">
              <TenantPicker tenants={choice.tenants} busy={busy} onPick={pickTenant} />
              <button className="btn ghost login-back" disabled={busy} onClick={() => setChoice(null)}>
                Back
              </button>
            </div>
          ) : (
            <button className="btn login-ms" disabled={busy || (!viaIce && !msalEnabled)} onClick={microsoft}>
              <MicrosoftMark />
              {busy ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>
          )}

          {!viaIce && !msalEnabled && (
            <p className="hint login-msal-off">
              Microsoft sign-in is not configured (VITE_ENTRA_CLIENT_ID is unset).
            </p>
          )}
        </div>
      </div>

      <div className="auth-footer">
        <picture className="auth-mark">
          <source srcSet={icefieldLogoOnDark} media="(prefers-color-scheme: dark)" />
          <img src={icefieldLogo} alt="Icefield" className="auth-footer-logo" />
        </picture>
      </div>
    </div>
  )
}

/** The real four-colour Microsoft mark, not a generic glyph — this is specifically that button. */
function MicrosoftMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}
